import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Spin,
  Row,
  Col,
  Tooltip,
  Avatar,
  Divider,
} from "antd";
import {
  UploadOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ajaxService from "../../../services/ajax-service";

const { Option } = Select;

const UserEditForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchUser = async (userId) => {
    setLoading(true);
    try {
      const res = await ajaxService.get(`/users/${userId}`);
      if (res?.success) {
        setUser(res.data);
        setAvatarUrl(res.data.profile_image || null);
        form.setFieldsValue({
          ...res.data,
          dob: res.data.dob ? dayjs(res.data.dob) : null,
          gender: res.data.gender ? res.data.gender.toLowerCase() : undefined,
        });
      }
    } catch (err) {
      message.error("Error loading user details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchUser(id);
  }, [id]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values };
      if (values.dob) {
        payload.dob = values.dob.format("YYYY-MM-DD");
      }

      let res;
      if (avatarFile) {
        // Handle file upload with FormData
        const formData = new FormData();
        Object.keys(payload).forEach((key) => {
          formData.append(key, payload[key]);
        });
        formData.append("profile_image", avatarFile);
        res = await ajaxService.putFormData(`/users/`, id, formData);
      } else {
        // Regular payload
        res = await ajaxService.put(`/users/`, id, payload);
      }

      if (res?.success) {
        message.success("✅ User updated successfully");
        history.push(`/users/${id}`);
      } else {
        message.error(res?.message || "Update failed");
      }
    } catch (err) {
      message.error("Error updating user");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Card
        style={{
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-card)',
          background: 'var(--dark-bg-card)',
          border: '1px solid rgba(254, 2, 125, 0.2)',
        }}
        title={<span style={{ fontSize: '1.25rem', fontFamily: 'Poppins-Bold', color: 'var(--text-white)' }}>Edit User</span>}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
            style={{
              background: 'var(--primary-purple)',
              borderColor: 'var(--primary-purple)',
              color: 'white',
            }}
          >
            Back
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Avatar Section */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-xl)' }}>
            <Avatar
              size={100}
              src={avatarUrl}
              icon={!avatarUrl && <UserOutlined />}
              style={{
                boxShadow: 'var(--shadow-card)',
                border: '1px solid rgba(254, 2, 125, 0.2)',
              }}
            />
            <Upload
              name="profile_image"
              listType="picture-circle"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                const url = URL.createObjectURL(file);
                setAvatarUrl(url);
                setAvatarFile(file);
                return false; // prevent auto-upload
              }}
              accept="image/*"
            >
              <Button 
                icon={<UploadOutlined />}
                style={{
                  background: 'var(--primary-purple)',
                  borderColor: 'var(--primary-purple)',
                  color: 'white',
                }}
              >
                Change Avatar
              </Button>
            </Upload>
          </div>

          <Row gutter={24}>
            {/* Left Column - Profile Info */}
            <Col xs={24} md={12}>
              <Card
                size="small"
                style={{
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-card)',
                  marginBottom: 'var(--spacing-lg)',
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                }}
              >
                <p style={{ fontWeight: 600, color: 'var(--text-white)', marginBottom: 'var(--spacing-sm)' }}>
                  Profile Information
                </p>
                <Divider style={{ margin: 'var(--spacing-sm) 0' }} />

                <Form.Item
                  name="name"
                  label={<span style={{ color: 'var(--text-white)' }}>Full Name</span>}
                  rules={[{ required: true, message: "Name is required" }]}
                  tooltip={{
                    title: "This will appear on the profile and reports",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Input 
                    placeholder="Enter full name" 
                    className="input-field"
                  />
                </Form.Item>

                {/* <Form.Item 
                  name="user_name" 
                  label={<span style={{ color: 'var(--text-white)' }}>Username</span>}
                >
                  <Input 
                    placeholder="Enter username" 
                    className="input-field"
                  />
                </Form.Item> */}

                <Form.Item
                  name="gender"
                  label={<span style={{ color: 'var(--text-white)' }}>Gender</span>}
                  rules={[{ required: true, message: "Gender is required" }]}
                >
                  <Select 
                    placeholder="Select gender"
                    className="input-field"
                  >
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </Form.Item>

                <Form.Item 
                  name="dob" 
                  label={<span style={{ color: 'var(--text-white)' }}>Date of Birth</span>}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    className="input-field"
                    disabled
                  />
                </Form.Item>

                <Form.Item 
                  name="bio" 
                  label={<span style={{ color: 'var(--text-white)' }}>Bio</span>}
                >
                  <Input.TextArea 
                    rows={3} 
                    placeholder="Write a short bio..." 
                    className="input-field"
                  />
                </Form.Item>
              </Card>
            </Col>

            {/* Right Column - Contact Info */}
            <Col xs={24} md={12}>
              <Card
                size="small"
                style={{
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-card)',
                  marginBottom: 'var(--spacing-lg)',
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                }}
              >
                <p style={{ fontWeight: 600, color: 'var(--text-white)', marginBottom: 'var(--spacing-sm)' }}>
                  Contact & Roles
                </p>
                <Divider style={{ margin: 'var(--spacing-sm) 0' }} />

                <Form.Item
                  name="email"
                  label={<span style={{ color: 'var(--text-white)' }}>Email</span>}
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Invalid email format" },
                  ]}
                >
                  <Input 
                    placeholder="Enter email" 
                    className="input-field"
                  />
                </Form.Item>

                <Form.Item 
                  name="phone" 
                  label={<span style={{ color: 'var(--text-white)' }}>Phone</span>}
                >
                  <Input 
                    placeholder="Enter phone number" 
                    className="input-field"
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          {/* Submit Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 'var(--spacing-lg)', 
            marginTop: 'var(--spacing-xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
          }}>
            <Button 
              onClick={() => history.goBack()}
              style={{
                background: 'var(--dark-bg-card)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)',
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
              }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default UserEditForm;